import type { CourseQuizContent } from '../types'

export const moonshotsContent: CourseQuizContent = {
  courseSlug:
    'breakthrough-innovation-strategy-from-bold-idea-to-business-impact',
  courseName: 'Breakthrough Innovation Strategy',

  goalQuestion: {
    title: 'Where do you hope to bring your moonshot idea to life?',
    options: [
      {
        label: 'Inside a big company with process, approvals, and politics.',
        value: 'big-company',
      },
      {
        label:
          'In a startup or small team, with limited resourcing and no spare budget.',
        value: 'startup',
      },
      {
        label:
          'On my own, with a venture or idea that’s completely in my control.',
        value: 'on-my-own',
      },
    ],
  },

  levelQuestion: {
    title: 'What’s standing between you and executing your next big idea?',
    options: [
      {
        label: 'Identifying a problem worth 10X ambition.',
        value: 'finding-it',
      },
      {
        label: 'Selling it: I have the idea, I need believers and budget.',
        value: 'selling-it',
      },
      {
        label: 'Proving it: testing and de-risking it fast.',
        value: 'proving-it',
      },
      {
        label: 'Scaling it: getting my team to think and act bigger.',
        value: 'spreading-it',
      },
    ],
  },

  primaryQuestionSlot: 'level',

  interstitial1: {
    pill: 'IN THIS COURSE',
    headline:
      'You’ll go inside Google’s moonshot factory, and leave with a pitch-ready Moonshot Launch Plan.',
    body: 'Learn the Moonshot Blueprint, tackle the riskiest part first, set kill criteria, and prototype fast, using the system behind Waymo and Wing.',
    image: 'https://static.masterclass.com/moonshot-interstitial-1.png',
    imageAlt: 'The Moonshot Launch Plan course materials',
  },

  interstitial2: {
    pill: 'DID YOU KNOW',
    headline:
      'You don’t need Google’s budget to innovate. You need their method.',
    body: 'X, Google’s moonshot factory, makes bold ideas cheap to test with scrappy prototypes, fast kills, protected sandboxes, and experiments sized to what you have. It works inside normal companies, small teams, and side projects.',
    image: 'https://static.masterclass.com/moonshot-interstitial-2.png',
    imageAlt: 'A team prototyping a bold idea',
  },

  profiles: [
    {
      key: 'finding-it',
      headline:
        'You want to identify the problem that’s worth 10X your ambition',
      lead: 'You know there’s a bigger problem worth solving, but you haven’t quite figured out how to frame it. This course gives you X’s method for identifying and shaping an idea worth a year of your effort.',
      bullets: [
        {
          text: 'Use **X’s brainstorming technique** to draft real moonshot candidates, not incremental tweaks.',
        },
        {
          text: '**Write your what-if statement and mission**, and shape them into a working Moonshot Blueprint.',
        },
        {
          text: '**Pressure-check your market and readiness** before you commit a year to the wrong idea.',
        },
      ],
    },
    {
      key: 'selling-it',
      headline:
        'You believe in your big idea, but you need funding and others to believe in it too',
      lead: 'The idea is real. What’s missing is support. This course teaches the storytelling and coalition-building that helped launch Wing and Loon, so your moonshot gets backed before it’s built.',
      bullets: [
        {
          text: 'Craft a **moonshot story** with X’s Chief Storyteller.',
        },
        {
          text: '**Remix the story for every audience**: the CFO, the skeptic, the partner, the press.',
        },
        {
          text: '**Map your coalition**, internal and external, and give every ally a named role.',
        },
      ],
    },
    {
      key: 'proving-it',
      headline: 'You want to prove your idea fast, or kill it while it’s cheap',
      lead: 'You’re done with hypothetical slides. You need evidence. This course teaches X’s approach to testing ideas, so the weak ones die early, and the strong ones earn their next step.',
      bullets: [
        {
          text: '**Run premortems and red-team exercises** on your own idea before reality does it for you.',
        },
        {
          text: '**Identify your monkey**, X’s rule for attacking the hardest, riskiest part first.',
        },
        {
          text: '**Prototype Design Kitchen-style**, rough and fast, then speed it up with AI prototyping tools.',
        },
      ],
    },
    {
      key: 'spreading-it',
      headline:
        'You’re ready to scale your idea and want to push your team to think and act bigger',
      lead: 'Your biggest challenge is a team that thinks small because failure costs too much. This course shows you how to lower that price and build a culture that aims for 10X.',
      bullets: [
        {
          text: '**Audit your own thought patterns and your organization’s culture** for what’s quietly capping ambition.',
        },
        {
          text: '**Design a protected sandbox** where bold ideas can be tried without career risk.',
        },
        {
          text: '**Set an Audacious Challenge**, X’s alternative to milestones, that pulls your team toward bigger thinking.',
        },
      ],
    },
  ],

  paceNotes: [
    { key: 'lt1h', weeks: '20 to 24 weeks' },
    { key: '1to3h', weeks: '7 to 10 weeks' },
    { key: '3to5h', weeks: '4 to 5 weeks' },
    { key: '5plush', weeks: '3 to 4 weeks' },
  ],

  previewLessons: [
    {
      title: 'This Is Where Bold Begins',
      mediaUuid: 'b1a8ee58-8966-4907-a8c7-638ad37d8c2e',
      image: 'https://static.masterclass.com/moonshot-video-1.png',
    },
    {
      title: '"What If" Before "What Is"',
      mediaUuid: 'dd827336-2597-4e19-b2da-915b922c67bc',
      image: 'https://static.masterclass.com/moonshot-video-2.png',
    },
  ],

  ctaHighlights: [
    'Taught by leaders of X, Google’s Moonshot Factory.',
    'Create your pitch-ready Moonshot Launch Plan.',
    'Earn a certificate signed by Astro Teller.',
  ],
}
